/**
 * Utilitários de formatação padronizados para o projeto Joshua
 *
 * FONTE ÚNICA DE VERDADE para formatação de dados no projeto.
 */

type Numericish = number | string | null | undefined;
type Stringish = string | null | undefined;

// ========================================
// MOEDA
// ========================================

/**
 * Formata valor como moeda brasileira SEM prefixo R$
 * Usado em tabelas e inputs onde o label já indica "(R$)"
 *
 * @example formatMoney(1234.5) → "1.234,50"
 */
export function formatMoney(value: Numericish): string {
  const numValue = parseFloat(String(value ?? '')) || 0;
  return new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(numValue);
}

/**
 * Formata valor como moeda brasileira COM prefixo R$
 * Usado em PDFs e relatórios
 *
 * @example formatCurrency(1234.5) → "R$ 1.234,50"
 * @example formatCurrency(1234.5, { showSymbol: false }) → "1.234,50"
 */
type FormatCurrencyOptions = {
  currency?: string;
  locale?: string;
  showSymbol?: boolean;
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
};

export function formatCurrency(value: Numericish, options: FormatCurrencyOptions = {}): string {
  const {
    currency = 'BRL',
    locale = 'pt-BR',
    showSymbol = true,
    minimumFractionDigits = 2,
    maximumFractionDigits = 2,
  } = options;

  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return showSymbol ? 'R$ 0,00' : '0,00';
  }

  const numValue = typeof value === 'string' ? parseFloat(value) : value;

  if (showSymbol) {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      minimumFractionDigits,
      maximumFractionDigits,
    }).format(numValue);
  }

  return new Intl.NumberFormat(locale, {
    minimumFractionDigits,
    maximumFractionDigits,
  }).format(numValue);
}

// ========================================
// NÚMEROS
// ========================================

/**
 * Formata número com separadores e casas decimais
 *
 * @example formatNumber(1234.5)    → "1.234,50"
 * @example formatNumber(1234, 0)   → "1.234"
 */
export function formatNumber(value: Numericish, decimals = 2, locale = 'pt-BR'): string {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return '0';
  }

  const numValue = typeof value === 'string' ? parseFloat(value) : value;

  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(numValue);
}

/**
 * Formata número inteiro (quantidade, sem decimais)
 *
 * @example formatQuantity(1234) → "1.234"
 */
export function formatQuantity(value: Numericish): string {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return '0';
  }
  return formatNumber(value, 0);
}

/**
 * Formata valor como percentual
 *
 * @example formatPercent(15.5)                    → "15,50%"
 * @example formatPercent(0.155, { isDecimal: true }) → "15,50%"
 */
type FormatPercentOptions = { decimals?: number; isDecimal?: boolean };

export function formatPercent(
  value: Numericish,
  options: FormatPercentOptions | number = {}
): string {
  const decimals = typeof options === 'number' ? options : (options.decimals ?? 2);
  const isDecimal = typeof options === 'object' ? !!options.isDecimal : false;

  const numValue = parseFloat(String(value ?? '')) || 0;
  const displayValue = isDecimal ? numValue * 100 : numValue;
  return `${formatNumber(displayValue, decimals)}%`;
}

// ========================================
// DATAS
// ========================================

/**
 * Formata data ISO (YYYY-MM-DD) para formato brasileiro (DD/MM/YYYY)
 *
 * IMPORTANTE: Protege contra bug de timezone UTC.
 * Quando o JS faz new Date("2026-02-11"), interpreta como UTC meia-noite,
 * e no fuso do Brasil (UTC-3) vira 2026-02-10 21:00:00 — mostrando dia errado.
 * Esta função adiciona T12:00:00 para evitar o problema.
 *
 * @example formatDate("2026-02-11") → "11/02/2026"
 * @example formatDate(null)         → "-"
 */
export function formatDate(value: string | Date | null | undefined): string {
  if (!value) return '-';
  const asString = typeof value === 'string' ? value : String(value);
  const date = new Date(asString + (typeof value === 'string' && !asString.includes('T') ? 'T12:00:00' : ''));
  if (isNaN(date.getTime())) return '-';
  // eslint-disable-next-line no-restricted-syntax -- implementação central, timezone já protegido
  return date.toLocaleDateString('pt-BR');
}

/**
 * Formata data com opções de formato (para PDFs e relatórios)
 *
 * @example formatDateLong("2026-02-11")               → "11 de fevereiro de 2026"
 * @example formatDateLong("2026-02-11", 'full')       → "quarta-feira, 11 de fevereiro de 2026"
 * @example formatDateLong("2026-02-11", 'short', true) → "11/02/2026 14:30"
 */
type DateFormat = 'short' | 'long' | 'full';

export function formatDateLong(
  value: string | Date | null | undefined,
  format: DateFormat = 'long',
  includeTime = false
): string {
  if (!value) return '-';

  const asString = typeof value === 'string' ? value : String(value);
  const date = new Date(asString + (typeof value === 'string' && !asString.includes('T') ? 'T12:00:00' : ''));
  if (isNaN(date.getTime())) return '-';

  const dateOptions: Record<DateFormat, Intl.DateTimeFormatOptions> = {
    short: { day: '2-digit', month: '2-digit', year: 'numeric' },
    long: { day: 'numeric', month: 'long', year: 'numeric' },
    full: { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' },
  };

  const timeOptions: Intl.DateTimeFormatOptions = includeTime ? { hour: '2-digit', minute: '2-digit' } : {};

  return new Intl.DateTimeFormat('pt-BR', {
    ...dateOptions[format],
    ...timeOptions,
  }).format(date);
}

/**
 * Formata data/hora ISO para formato brasileiro (DD/MM/YYYY HH:mm)
 *
 * @example formatDateTime("2026-02-11T14:30:00") → "11/02/2026 14:30:00"
 */
export function formatDateTime(value: string | Date | null | undefined): string {
  if (!value) return '-';
  const date = new Date(value);
  if (isNaN(date.getTime())) return '-';
  return date.toLocaleString('pt-BR');
}

// ========================================
// DOCUMENTOS BRASILEIROS
// ========================================

/**
 * Formata telefone brasileiro
 *
 * @example formatPhone("11999998888") → "(11) 99999-8888"
 */
export function formatPhone(phone: Stringish): string {
  if (!phone) return '';
  const numbers = String(phone).replace(/\D/g, '');

  if (numbers.length === 11) {
    return numbers.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
  } else if (numbers.length === 10) {
    return numbers.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
  } else if (numbers.length === 8) {
    return numbers.replace(/(\d{4})(\d{4})/, '$1-$2');
  }

  return String(phone);
}

/**
 * Formata CPF ou CNPJ
 *
 * @example formatDocument("12345678901")   → "123.456.789-01"
 * @example formatDocument("12345678000190") → "12.345.678/0001-90"
 */
export function formatDocument(document: Stringish): string {
  if (!document) return '';
  const numbers = String(document).replace(/\D/g, '');

  if (numbers.length === 11) {
    return numbers.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  } else if (numbers.length === 14) {
    return numbers.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  }

  return String(document);
}

/**
 * Formata CEP
 *
 * @example formatCEP("01234567") → "01234-567"
 */
export function formatCEP(cep: Stringish): string {
  if (!cep) return '';
  const numbers = String(cep).replace(/\D/g, '');

  if (numbers.length === 8) {
    return numbers.replace(/(\d{5})(\d{3})/, '$1-$2');
  }

  return String(cep);
}

type Endereco = {
  logradouro?: string | null;
  rua?: string | null;
  numero?: string | number | null;
  complemento?: string | null;
  bairro?: string | null;
  cidade?: string | null;
  localidade?: string | null;
  uf?: string | null;
  estado?: string | null;
  cep?: string | null;
};

/**
 * Formata endereço completo
 */
export function formatAddress(endereco: Endereco | null | undefined): string {
  if (!endereco) return '';

  const parts: string[] = [];

  const logradouro = endereco.logradouro || endereco.rua;
  if (logradouro) {
    parts.push(logradouro);
  }

  if (endereco.numero) {
    parts[parts.length - 1] += `, ${endereco.numero}`;
  }

  if (endereco.complemento) {
    parts[parts.length - 1] += ` - ${endereco.complemento}`;
  }

  if (endereco.bairro) {
    parts.push(endereco.bairro);
  }

  if (endereco.cidade || endereco.localidade) {
    const cidade = endereco.cidade || endereco.localidade || '';
    const uf = endereco.uf || endereco.estado;
    parts.push(uf ? `${cidade}/${uf}` : cidade);
  }

  if (endereco.cep) {
    parts.push(`CEP: ${formatCEP(endereco.cep)}`);
  }

  return parts.join(' - ');
}

// ========================================
// TEXTO
// ========================================

/**
 * Trunca texto com reticências
 *
 * @example truncate("texto longo", 8) → "texto..."
 */
export function truncate(text: Stringish, maxLength = 50): string {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength - 3) + '...';
}

/**
 * Capitaliza primeira letra
 */
export function capitalize(text: Stringish): string {
  if (!text) return '';
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
}

/**
 * Formata nome próprio (capitaliza cada palavra, preserva preposições)
 */
export function formatName(name: Stringish): string {
  if (!name) return '';

  const prepositions = ['de', 'da', 'do', 'das', 'dos', 'e'];

  return name
    .toLowerCase()
    .split(' ')
    .map((word: string, index: number) => {
      if (index > 0 && prepositions.includes(word)) {
        return word;
      }
      return capitalize(word);
    })
    .join(' ');
}

// ========================================
// CÓDIGOS DO SISTEMA
// ========================================

/**
 * Formato Banco: OSZAFBRA26010001 (sem hífen)
 * Formato UI:    OS-ZAFBRA26010001 (com hífen)
 * Legado:        2025030075 (YYYYMMXXXX)
 */

/**
 * Formata código para exibição na UI (adiciona hífen)
 *
 * @example formatCodigo('OSZAFBRA26010001') → 'OS-ZAFBRA26010001'
 */
export function formatCodigo(codigo: Stringish): string {
  if (!codigo) return '';

  const match = codigo.match(/^(CAT|REQ|OC|OS|OP|PC)(.+)$/);
  if (match) {
    return `${match[1]}-${match[2]}`;
  }

  return codigo;
}

/**
 * Remove formatação do código (remove hífen) para uso em queries
 *
 * @example normalizeCodigo('OS-ZAFBRA26010001') → 'OSZAFBRA26010001'
 */
export function normalizeCodigo(codigo: Stringish): string {
  if (!codigo) return '';
  return codigo.replace(/-/g, '');
}

// ========================================
// MARKDOWN SIMPLES
// ========================================

/**
 * Parseia markdown simples (apenas **negrito**) para array de segmentos
 * Retorna null se não houver marcadores, permitindo fallback para texto puro.
 *
 * @example parseSimpleMarkdown("texto **negrito** normal")
 *   → ["texto ", { bold: true, text: "negrito" }, " normal"]
 * @example parseSimpleMarkdown("sem negrito") → null
 */
type MarkdownSegment = string | { bold: true; text: string };

export function parseSimpleMarkdown(text: unknown): MarkdownSegment[] | null {
  if (!text || typeof text !== 'string' || !text.includes('**')) return null;

  const segments: MarkdownSegment[] = [];
  const regex = /\*\*(.*?)\*\*/g;
  let lastIndex = 0;
  let match;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      segments.push(text.slice(lastIndex, match.index));
    }
    segments.push({ bold: true, text: match[1] ?? '' });
    lastIndex = regex.lastIndex;
  }

  if (lastIndex < text.length) {
    segments.push(text.slice(lastIndex));
  }

  return segments;
}

// ========================================
// DISPATCHER
// ========================================

type FormatValueType =
  | 'currency'
  | 'money'
  | 'date'
  | 'date-short'
  | 'date-short-year'
  | 'datetime'
  | 'datetime-short'
  | 'datetime-short-year'
  | 'number'
  | 'quantity'
  | 'percent'
  | 'phone'
  | 'document'
  | 'cep'
  | 'name';

type FormatValueOptions = FormatCurrencyOptions & FormatPercentOptions & {
  fallback?: string;
};

/**
 * Formata valor baseado no tipo (útil para componentes genéricos)
 */
export function formatValue(
  value: unknown,
  format: FormatValueType | string,
  options: FormatValueOptions = {}
): string {
  if (value === null || value === undefined) return options.fallback || '';

  const numericValue = value as Numericish;
  const stringValue = value as Stringish;

  const toStrDate = (v: unknown): string => typeof v === 'string' ? v : String(v);

  switch (format) {
    case 'currency':
      return formatCurrency(numericValue, options);
    case 'money':
      return formatMoney(numericValue);
    case 'date':
      return formatDate(value as string | Date | null | undefined);
    case 'date-short': {
      const s = toStrDate(value);
      const d1 = new Date(s + (typeof value === 'string' && !s.includes('T') ? 'T12:00:00' : ''));
      if (isNaN(d1.getTime())) return '-';
      return d1.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }); // eslint-disable-line no-restricted-syntax
    }
    case 'date-short-year': {
      const s = toStrDate(value);
      const d2 = new Date(s + (typeof value === 'string' && !s.includes('T') ? 'T12:00:00' : ''));
      if (isNaN(d2.getTime())) return '-';
      return d2.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' }); // eslint-disable-line no-restricted-syntax
    }
    case 'datetime': {
      const d3 = new Date(value as string | Date);
      if (isNaN(d3.getTime())) return '-';
      return d3.toLocaleDateString('pt-BR') + ' às ' + d3.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }); // eslint-disable-line no-restricted-syntax
    }
    case 'datetime-short': {
      const d4 = new Date(value as string | Date);
      if (isNaN(d4.getTime())) return '-';
      return d4.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }) + ' às ' + d4.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }); // eslint-disable-line no-restricted-syntax
    }
    case 'datetime-short-year': {
      const d5 = new Date(value as string | Date);
      if (isNaN(d5.getTime())) return '-';
      return d5.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' }) + ' às ' + d5.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }); // eslint-disable-line no-restricted-syntax
    }
    case 'number':
      return formatNumber(numericValue, options.decimals);
    case 'quantity':
      return formatQuantity(numericValue);
    case 'percent':
      return formatPercent(numericValue, options);
    case 'phone':
      return formatPhone(stringValue);
    case 'document':
      return formatDocument(stringValue);
    case 'cep':
      return formatCEP(stringValue);
    case 'name':
      return formatName(stringValue);
    default:
      return String(value);
  }
}
